import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";

const initialState = {
  professionals: [],
  professionalDetails: null,
  isLoading: false,
  errors: null,
  success: false,
  lastFetchedAt: null,
};

const ProfessionalSlice = createSlice({
  name: "professional",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setProfessionals: (state, action) => {
      state.professionals = action.payload;
      state.lastFetchedAt = Date.now();
    },
    setProfessionalDetails: (state, action) => {
      state.professionalDetails = action.payload;
    },
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    clearProfessionalState: (state) => {
      state.professionalDetails = null;
      state.errors = null;
      state.success = false;
    }
  },
});

export const {
  setLoading,
  setProfessionals,
  setProfessionalDetails,
  setErrors,
  setSuccess,
  clearProfessionalState
} = ProfessionalSlice.actions;

/**
 * Fetch the current provider's professional services.
 * GET /professional/me/services
 */
export const getMyProfessionals = (force = false) => async (dispatch, getState) => {
  const { professionals, lastFetchedAt, isLoading } = getState().professional;
  const FIVE_MINUTES = 5 * 60 * 1000;

  if (
    !force &&
    professionals.length > 0 &&
    lastFetchedAt &&
    Date.now() - lastFetchedAt < FIVE_MINUTES
  ) return;

  if (isLoading) return;

  dispatch(setLoading(true));
  dispatch(setErrors(null));

  try {
    const URL = `professional/me/services`;
    const response = await axiosInstance.get(URL);
    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(setProfessionals(data));
      dispatch(setSuccess(true));
    } else {
      dispatch(setErrors({ message: message || 'Failed to fetch professionals' }));
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Failed to load professionals';
    dispatch(setErrors({ message: errorMsg }));
  } finally {
    dispatch(setLoading(false));
  }
};

/** 
 * Create a new Professional Profile
 * POST /professional
 */
export const createProfessional = (professionalData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));
  dispatch(setSuccess(false));

  try {
    let finalPayload;
    
    // Industrial Context Check: If already FormData, pass through. Otherwise build it.
    if (professionalData instanceof FormData) {
      finalPayload = professionalData;
    } else {
      const formData = new FormData();

      // ─── Industrial Hierarchy IDs (BZV2) ──────────────────────────────────
      formData.append('workspaceClassId', professionalData.workspaceClassId);
      if (professionalData.professionalTypeId) {
        formData.append('professionalTypeId', professionalData.professionalTypeId);
      }

      // ─── Lean Service Data (Overrides) ────────────────────────────────────
      if (professionalData.description) formData.append('description', professionalData.description);
      
      if (professionalData.address) {
        // Handle address object expansion for legacy support
        if (typeof professionalData.address === 'object') {
           formData.append('address[street]', professionalData.address.building || '');
           formData.append('address[area]', professionalData.address.area || '');
           formData.append('address[city]', professionalData.address.city || '');
           formData.append('address[state]', professionalData.address.state || '');
           formData.append('address[pincode]', professionalData.address.pincode || '');
           
           if (professionalData.address.latitude) {
             formData.append('address[latitude]', professionalData.address.latitude);
             formData.append('address[longitude]', professionalData.address.longitude);
           }
        } else {
           formData.append('address', professionalData.address);
        }
      }

      // ─── Media & Session Context ──────────────────────────────────────────
      if (professionalData.profilePicture) {
        formData.append('profilePicture', professionalData.profilePicture);
      }

      if (professionalData.workspaceId) {
        formData.append('workspaceId', professionalData.workspaceId);
      }
      
      finalPayload = formData;
    }

    // Hit the industrial workspace creation endpoint
    const URL = `workspaces/professional`;
    const response = await axiosInstance.post(URL, finalPayload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const { success, message, data } = response?.data || {};

    if (success) {
      dispatch(setSuccess(true));
      showNotification({
        message: message || "Professional profile created successfully!",
        type: "success",
      });
      return { success: true, data };
    } else {
      const errorMsg = message || "Failed to create professional profile.";
      dispatch(setErrors({ message: errorMsg }));
      showNotification({ message: errorMsg, type: "error" });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || "An error occurred.";
    dispatch(setErrors({ message: errorMsg }));
    showNotification({ message: errorMsg, type: "error" });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Update a professional profile
 */
export const updateProfessionalProfile = (businessId, profileData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));
  dispatch(setSuccess(false));

  try {
    let finalPayload;
    if (profileData instanceof FormData) {
      finalPayload = profileData;
    } else {
      const formData = new FormData();
      Object.keys(profileData).forEach(key => {
        if (key === 'address' && typeof profileData[key] === 'object') {
          Object.keys(profileData[key]).forEach(addrKey => {
            const k = addrKey === 'building' ? 'street' : addrKey;
            formData.append(`address[${k}]`, profileData[key][addrKey]);
          });
        } else if (key === 'profileImage' || key === 'profilePicture' || key === 'imageFile') {
           if (profileData[key] instanceof File) {
              formData.append('profilePicture', profileData[key]);
           }
        } else if (key === 'workspaceId') {
          formData.append('workspaceId', profileData[key]);
        } else {
          formData.append(key, profileData[key]);
        }
      });
      finalPayload = formData;
    }

    const URL = `workspaces/professional/${businessId}`;
    const response = await axiosInstance.patch(URL, finalPayload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(setProfessionalDetails(data));
      dispatch(setSuccess(true));
      showNotification({ message: message || "Profile updated successfully!", type: "success" });
      return { success: true, data };
    } else {
      const errorMsg = message || "Update failed";
      dispatch(setErrors(errorMsg));
      showNotification({ message: errorMsg, type: "error" });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || "Update failed";
    dispatch(setErrors(errorMsg));
    showNotification({ message: errorMsg, type: "error" });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export default ProfessionalSlice.reducer;
